<template>
  <div>
    <NuxtLayout name="default" :is-primary-navbar-sticky="isDesktopScreen">
      <section class="bg-light-2 py-space-md py-lg-space-lg">
        <div class="container">
          <div class="row">
            <div class="col-12 col-lg-8">
              <h1 class="h1-large mb-space-sm">
                <!-- section-introduction -->
                {{ $page?.minorsPage?.title }}
              </h1>
              <div v-html="$page?.minorsPage?.description"></div>
            </div>
          </div>
        </div>
      </section>
      <section
        class="bg-light-2 pt-0 pb-space-lg pb-lg-space-xxl filter-categories"
      >
        <div class="container">
          <div class="row">
            <!-- filters column: visible only for desktop and above screens -->
            <div class="d-none d-lg-block col-lg-3">
              <div
                class="d-flex justify-content-between align-items-center pb-space-xs"
              >
                <h3 class="h3-medium">{{ filterTitle }}</h3>
                <button
                  v-if="hasAppliedFilters"
                  @click="handleClearAll()"
                  class="fs-small rds-link clear-btn"
                  data-cy="online-degree-programs-clear-all-filters-button"
                >
                  Clear all
                </button>
              </div>
              <!-- filter block -->
              <div class="py-lg-space-xxs border-bottom">
                <FilterCollapseCheckboxPlus
                  component-id="program-filters"
                  :title="PAGE_CONSTANTS.HIGHLIGHT_FILTER_TITLE"
                  @checked="handleCollegeChecked"
                  @unchecked="handleCollegeUnchecked"
                  v-model="selectedColleges"
                  :options="collegesFilterOptions"
                />
              </div>
            </div>
            <!-- search column -->
            <div class="col-12 col-lg-9" ref="mobileFilterButton">
              <!-- search query row: visible only for desktop and above screens -->
              <div id="search-query">
                <div class="col-12 mb-0">
                  <TypeAheadSearch
                    placeholder="Search program by title"
                    :options="filteredOptions"
                    v-model="queries"
                    @changed="handleSearchQueryChanged"
                    id="search-typeahead"
                  />
                </div>
              </div>
              <div
                class="row bg-light-2 pt-space-xs pt-lg-0 mb-space-sm"
                :class="{
                  'filter-div': !isDesktopScreen,
                  'sticky-top': !isDesktopScreen,
                  'pb-space-sm': !isDesktopScreen,
                }"
              >
                <div class="d-block d-lg-none col-12">
                  <!-- add mobile loading -->
                  <div
                    class="overlay-loading"
                    :class="{ 'overlay-loading--show': pageLoading }"
                  >
                    <spinner-group
                      class="overlay-loader"
                      v-if="pageLoading"
                      spinner-style="border"
                    ></spinner-group>
                    <button
                      class="btn btn-dark-3 w-100 d-bold fw-bold text-white"
                      @click="handleCanvasAsideShow"
                    >
                      {{ filterTitle }}
                    </button>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <p class="fw-small pt-space-sm pt-lg-0 fw-bold mb-space-sm">
                    {{ resultsFiltered?.length ?? 0 }} programs available
                  </p>
                  <div v-if="resultsFiltered?.length === 0" class="fs-small">
                    <p>
                      Sorry, no results were found based on your current search
                      criteria. Please try again.
                    </p>
                    <p>
                      <span class="fw-bold">Search suggestions:</span><br />
                      Check your spelling. <br />Clear your filters.<br />Try
                      different words that mean the same thing.<br />Try more
                      general words.
                    </p>
                  </div>
                </div>
              </div>
              <!-- search results row -->
              <template v-if="pageLoading">
                <div class="status text-center">
                  <spinner-group spinner-style="border"></spinner-group>
                </div>
              </template>
              <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3">
                <div
                  id="my-programs"
                  class="col mb-space-xs mb-lg-sm"
                  v-for="minor in paginatedData"
                  :key="minor.item.id"
                >
                  <CardTextAtlas
                    :title="minor.item.title as string"
                    is-clickable
                    display-footer
                    :display-label="false"
                    cta-link="javascript:void(0)"
                    cta-text="View details"
                    @card-click="handleModalClick(minor.item)"
                    class="h-100"
                  />
                </div>
              </div>
              <RdsPagination
                aria-controls=""
                ariaLabel="Pagination"
                v-model="currentPage"
                align="center"
                pills
                hide-ellipses
                class="mt-space-md degree-search-pagination"
                :perPage="PAGE_CONSTANTS.PER_PAGE"
                :hide-goto-end-buttons="true"
                label-next-page="go to next page"
                label-previous-page="go to previous page"
                :hide-goto-start-buttons="true"
                :limit="6"
                @change="handlePaginationChange"
                :totalRows="resultsFiltered.length"
              >
                <template #next-text="{ disabled, onClick }">
                  <button
                    class="btn btn-link d-flex text-dark-3 justify-content-center align-items-center fw-bold"
                    :disabled="disabled"
                    @click="
                      ($event) =>
                        handleChevronClick($event, onClick, 'right chevron')
                    "
                  >
                    <span class="sr-only">Next</span>
                    <font-awesome-icon :icon="['fas', 'chevron-right']" />
                  </button>
                </template>
                <template #prev-text="{ disabled, onClick }"
                  ><button
                    class="btn btn-link d-flex text-dark-3 justify-content-center align-items-center"
                    :disabled="disabled"
                    @click="
                      ($event) =>
                        handleChevronClick($event, onClick, 'left chevron')
                    "
                  >
                    <span class="sr-only">Previous</span>
                    <font-awesome-icon :icon="['fas', 'chevron-left']" />
                  </button>
                </template>
              </RdsPagination>
            </div>
          </div>
        </div>
      </section>

      <!-- requirement tabs -->
      <SectionIntroFalcon
        image-source-lazy
        :title="$page?.minorsPage.sectionTitle!"
        id="section-requirements"
        :image-source="$page?.minorsPage.tabMainImage!"
        container-bg-variant="white"
        :bg-image-source="$page?.minorsPage.tabBackgroundImage!"
        image-position="right"
      >
        <template #body>
          <div class="nav">
            <ul class="nav border-bottom" role="tablist">
              <li
                role="presentation"
                v-for="tab in tabContent ? Object.keys(tabContent) : []"
                class="nav-item fs-medium fw-bold"
                @click="handleTabClick(tab)"
                :key="tab"
              >
                <button
                  role="tab"
                  class="nav-link tab-item me-space-sm px-0 text-dark-3 pb-0"
                  :aria-selected="activeTab === tab"
                  :aria-controls="`college_minor_${tab}`"
                  :class="{ 'tab-item--active': activeTab === tab }"
                >
                  {{ tabContent?.[tab]?.title }}
                </button>
              </li>
            </ul>
          </div>
          <Transition name="fade" mode="out-in" appear :duration="100">
            <div class="tab-container" :key="activeTab">
              <div
                class="pt-space-md"
                v-html="tabContent?.[activeTab]?.preText"
              ></div>
              <div class="pb-space-xxs">
                <div
                  v-for="list in tabContent?.[activeTab]?.items"
                  class="d-flex align-items-top"
                >
                  <img
                    v-show="tabContent?.[activeTab]?.icon"
                    :src="(tabContent?.[activeTab]?.icon as string)"
                    :alt="(tabContent?.[activeTab]?.iconAlt as string)"
                    class="check-icon me-space-xs"
                  />
                  <div v-html="list" class="mb-space-xxs"></div>
                </div>
              </div>
              <div v-html="tabContent?.[activeTab]?.postText"></div>
            </div>
          </Transition>
        </template>
      </SectionIntroFalcon>
      <!-- section-rfi -->
      <SectionRfiMultiStep :bg-image="RfiBgImage"></SectionRfiMultiStep>

      <!-- minor description modal -->
      <RdsModal
        teleportLocation="body"
        size="lg"
        :visible="showModal"
        @hidden="hideModal"
        centered
      >
        <div class="px-space-md pb-space-md">
          <h3 class="h3-xl mb-space-sm mt-space-xxs">
            {{ modalValue?.title }}
          </h3>
          <div
            class="fs-medium mb-0"
            v-html="modalValue?.longDescription"
          ></div>
        </div>
      </RdsModal>
      <OffCanvasAside
        v-model="isOffCanvasAsideOpen"
        direction="left"
        width="320px"
        teleportTo="body"
        bgVariant="white"
      >
        <header class="border-bottom p-space-xs">
          <div class="row">
            <div class="col-5">
              <button
                @click="handleClearAll()"
                :disabled="!hasAppliedFilters"
                class="btn btn-light-2 fw-bold py-space-xxs d-block w-100"
              >
                Clear all
              </button>
            </div>
            <div class="col-7">
              <button
                @click="showMobileFilterResults"
                class="btn btn-secondary fw-bold py-space-xxs d-block w-100"
              >
                {{ sidebarApplyText }}
              </button>
            </div>
          </div>
        </header>
        <div class="sidebar-content px-space-xs overflow-y-auto">
          <!-- college filter block -->
          <div class="pb-space-xxs my-space-xs border-bottom filter-categories">
            <FilterCollapseCheckboxPlus
              component-id="program-filters"
              :title="mobileProgramText"
              @checked="handleCollegeChecked"
              @unchecked="handleCollegeUnchecked"
              @hidden="handleCollegeCollapseHidden"
              @shown="handleCollegeCollapseShown"
              v-model="selectedColleges"
              :options="collegesFilterOptions"
            />
          </div>
        </div>
      </OffCanvasAside>
    </NuxtLayout>
  </div>
</template>
<script setup lang="ts">
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CardTextAtlas } from "@rds-vue-ui/card-text-atlas";
import { RdsPagination } from "@rds-vue-ui/rds-pagination";
import { SectionIntroFalcon } from "@rds-vue-ui/section-intro-falcon";
import { debouncedRef, useWindowSize } from "@vueuse/core";
import { UseFuseOptions, useFuse } from "@vueuse/integrations/useFuse";
import { FuseResult } from "fuse.js";
import RfiBgImage from "~/assets/img/rfi-bg-overlay.png";
import OffCanvasAside from "~/components/OffCanvasAside.vue";
import SpinnerGroup from "~/components/SpinnerGroup.vue";
import TypeAheadSearch, {
  type TypeAheadSearchListOption,
} from "~/components/TypeAheadSearch.vue";
import FilterCollapseCheckboxPlus, {
  FilterCollapseOption,
} from "~/components/degree-search/FilterCollapseCheckboxPlus.vue";
import SectionRfiMultiStep from "~/components/rfi/SectionRfiMultiStep.vue";
import {
  MinorsPage,
  NonDegreeSeekingProgram,
  ParagraphTabItem,
} from "~/server/resolvers/resolvers-types";
import { extractUniqueValues } from "~/src/services/DegreeSearchService";
interface MinorsPageResponse {
  minorsPage: MinorsPage;
  allNonDegreeSeekingProgram: NonDegreeSeekingProgram[];
}

/*
 ********************************************
 * Page query
 ********************************************
 */
definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"],
  },
});
const $page = await usePageQuery<MinorsPageResponse>(
  `#graphql
      query MinorsPage($minorsPageId: ID!, $sort: sortInput) {
        minorsPage(id: $minorsPageId) {
          id
          title
          detailPage
          metaTitle
          metaDescription
          tabBackgroundImage
          tabMainImage
          sectionTitle
          description
          slug
          tabs{
            id
            internalId
            title
            preText
            icon
            iconAlt
            items
            postText
          }
        }
        allNonDegreeSeekingProgram(sort: $sort) {
          id
              title
              description
              college {
                id
                title
                slug
                description
              }
        }
      }
      `,
  {
    minorsPageId: "3bf2c037-3290-410a-97aa-d1173184ec7b",
    sort: {
      by: "title",
      order: "ASC",
    },
  },
  {
    key: "minors-search",
  }
);
/*
 ********************************************
 * Page metadata
 ********************************************
 */
const jsonLdMarkup = useSchemaService().generateMinorProgramSchema(
  $page?.minorsPage.title as string,
  $page?.minorsPage.detailPage as string,
  $page?.minorsPage.metaTitle as string,
  $page?.minorsPage.metaDescription as string
);
useHead({
  title: $page?.minorsPage.metaTitle,
  meta: [
    {
      name: "description",
      content: $page?.minorsPage.metaDescription,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: $page?.minorsPage.detailPage as string,
    },
  ],
  script: [jsonLdMarkup],
});

/*
 ********************************************
 * Enums
 ********************************************
 */
// using const enums to avoid runtime overhead
const PAGE_CONSTANTS = {
  HIGHLIGHT_FILTER_TITLE: "College",
  PER_PAGE: 12,
  UPDATE_TEXT: "Update",
  CLOSE_TEXT: "Close",
  FILTERS_TEXT: "Filters",
  SECTION_TITLE: $page?.minorsPage?.title || "",
};
/*
 ********************************************
 * Data
 ********************************************
 */
const selectedColleges = ref<Set<string>>(new Set());
const isOffCanvasAsideOpen = ref<boolean>(false);
const pageLoading = ref<boolean>(true);
const activeTab = ref<string>("N/A");
/*
 ********************************************
 * Computed
 ********************************************
 */

const filteredOptions = computed<TypeAheadSearchListOption[]>(() => {
  return (
    resultsFiltered.value.map(({ item }) => {
      return {
        value: item?.title!,
        text: item?.title!,
      }!;
    }) || []
  );
});
const sidebarApplyText = computed<string>(() => {
  if (hasAppliedFilters.value) {
    return `${PAGE_CONSTANTS.UPDATE_TEXT} (${totalAppliedFilters.value})`;
  }
  return PAGE_CONSTANTS.CLOSE_TEXT;
});

const totalAppliedFilters = computed<number>(() => {
  return selectedColleges.value.size;
});
const hasAppliedFilters = computed<boolean>(() => {
  return totalAppliedFilters.value > 0;
});
const filterTitle = computed<string>(() => {
  if (hasAppliedFilters.value) {
    return `${PAGE_CONSTANTS.FILTERS_TEXT} (${totalAppliedFilters.value})`;
  }
  return PAGE_CONSTANTS.FILTERS_TEXT;
});

const collegesFilterOptions = computed<FilterCollapseOption[]>(() => {
  const options = extractUniqueValues(
    $page?.allNonDegreeSeekingProgram ?? [],
    "college"
  );
  return options.sort((a, b) => a.text.localeCompare(b.text));
});

/*
 ********************************************
 * Methods
 ********************************************
 */
const hideModal = () => {
  showModal.value = false;
  analyticsComposable.trackModalEvent(
    "onclick",
    "close",
    "click",
    "main content",
    modalValue.value?.title?.toLowerCase() || "N/A",
    "close cross",
    "minor modal"
  );
  modalValue.value = null;
};
const handleClearAll = () => {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    "filters",
    "clear all",
    "filters"
  );
  selectedColleges.value.clear();
};

const mobileFilterButton = ref<HTMLElement | null>(null);
const showMobileFilterResults = () => {
  // scroll to searchbar
  mobileFilterButton.value?.scrollIntoView({
    behavior: "smooth",
    block: "start",
    inline: "nearest",
  });
  analyticsComposable.trackLinkEvent(
    "internal link",
    "navbar",
    "main navbar",
    PAGE_CONSTANTS.SECTION_TITLE.toLowerCase(),
    "filters"
  );
  isOffCanvasAsideOpen.value = false;
};
const modalValue = ref<{
  title: string;
  longDescription: string;
} | null>(null);
const showModal = ref<boolean>(false);
const handleModalShow = (title: string, longDescription: string) => {
  showModal.value = true;
  modalValue.value = {
    title,
    longDescription,
  };
};

/*
 ********************************************
 * Analytics
 ********************************************
 */
const handleCanvasAsideShow = () => {
  isOffCanvasAsideOpen.value = true;
  analyticsComposable.trackModalEvent(
    "onclick",
    "open",
    "click",
    "main content",
    PAGE_CONSTANTS.SECTION_TITLE.toLowerCase(),
    "filters",
    "filters"
  );
};
const handleModalClick = ({ title, description }: NonDegreeSeekingProgram) => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "open",
    "click",
    "main content",
    PAGE_CONSTANTS.SECTION_TITLE?.toLowerCase(),
    title?.toLowerCase() as string
  );
  handleModalShow(title as string, description as string);
};
const handleCollegeChecked = (option: FilterCollapseOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "checked",
    "checkbox",
    "main content",
    "colleges",
    option.text.toLowerCase(),
    "filters"
  );
};
const handleCollegeUnchecked = (option: FilterCollapseOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "unchecked",
    "checkbox",
    "main content",
    "colleges",
    option.text.toLowerCase(),
    "filters"
  );
};

const handleCollegeCollapseHidden = () => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    "close",
    "click",
    "main content",
    "filters",
    "-"
  );
};
const handleCollegeCollapseShown = () => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    "open",
    "click",
    "main content",
    "filters",
    "+"
  );
};
const mobileProgramText = computed(() => {
  if (selectedColleges.value.size <= 0) {
    return PAGE_CONSTANTS.HIGHLIGHT_FILTER_TITLE;
  }
  return `${PAGE_CONSTANTS.HIGHLIGHT_FILTER_TITLE}(${totalAppliedFilters.value})`;
});
interface SearchOption {
  value: string;
  text: string;
}
const handleSearchQueryChanged = (searchOption: SearchOption) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "search",
    "main content",
    PAGE_CONSTANTS.SECTION_TITLE.toLowerCase(),
    searchOption.text.toLocaleLowerCase(),
    "search bar"
  );
};
const handleChevronClick = (
  event: Event,
  onClick: ($event: Event) => void,
  direction: string
) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "pagination",
    "main content",
    PAGE_CONSTANTS.SECTION_TITLE.toLowerCase(),
    direction,
    "pagination"
  );
  onClick(event);
};

/*
 ********************************************
 * Reactive
 ********************************************
 */
const { width } = useWindowSize();
const isDesktopScreen = computed(() => width.value >= 992);

/*
 ********************************************
 * Fuse search
 ********************************************
 */

const fuseOptions: UseFuseOptions<NonDegreeSeekingProgram> = {
  fuseOptions: {
    // set minMatchCharLength to 1 otherwise search bar will not filter programs with long title
    isCaseSensitive: false,
    includeScore: true,
    useExtendedSearch: true,
    includeMatches: true,
    minMatchCharLength: 1,
    shouldSort: true,
    findAllMatches: false,
    location: 0,
    threshold: 0.6,
    distance: 200,
    keys: ["title", "college.slug"],
  },
  matchAllWhenSearchEmpty: true,
};
const queries = ref<string | null>(null);
const searchOptions = ref<NonDegreeSeekingProgram[]>(
  ($page?.allNonDegreeSeekingProgram ?? []) as NonDegreeSeekingProgram[]
);
const resultsFiltered = ref<FuseResult<NonDegreeSeekingProgram>[]>([]);
// matchAllWhenSearchEmpty: true, to show all programs when search bar is empty
const { fuse, results } = useFuse("", searchOptions, fuseOptions);
resultsFiltered.value = results.value;

const debouncedSearchQuery = debouncedRef(queries, 350);

const preparedSchema = computed(() => {
  const schema = [];
  if (selectedColleges.value.size > 0) {
    schema.push({
      $or: Array.from(selectedColleges.value).map((college) => {
        return { "college.slug": `=${college}` };
      }),
    });
  }
  if (debouncedSearchQuery.value && debouncedSearchQuery.value.length > 0) {
    schema.push({ title: debouncedSearchQuery.value });
    analyticsComposable.trackEvent({
      event: "search",
      name: "onenter",
      action: "type",
      type: "search program by title",
      region: "main content",
      section: PAGE_CONSTANTS.SECTION_TITLE.toLowerCase(),
      text: debouncedSearchQuery.value.toLowerCase(),
      component: "minor search bar",
    });
  }
  return schema;
});

type TabType = {
  [key: string]: ParagraphTabItem;
};

const tabContent = computed<TabType | null>(() => {
  if (!$page?.minorsPage?.tabs) return null;
  return {
    ...$page?.minorsPage?.tabs?.reduce((acc: TabType, tab) => {
      if (tab && tab.id && acc !== null) {
        acc[tab?.id] = tab as ParagraphTabItem;
      }
      return acc;
    }, {}),
  };
});

const handleTabClick = (tab: string) => {
  activeTab.value = tab;
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    PAGE_CONSTANTS.SECTION_TITLE.toLowerCase(),
    tabContent.value?.[tab]?.title?.toLowerCase() || "N/A"
  );
};
const currentPage = ref<number>(1);
const paginatedData = ref<FuseResult<NonDegreeSeekingProgram>[]>([]);

const handlePaginationChange = (page: number) => {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "pagination",
    "main content",
    PAGE_CONSTANTS.SECTION_TITLE.toLowerCase(),
    `page ${page}`,
    "pagination"
  );
  fetchData();
  mobileFilterButton.value?.scrollIntoView({
    behavior: "smooth",
    block: "start",
    inline: "nearest",
  });
};
onMounted(() => {
  const { college } = useRoute().query;
  if (college && college !== "all-colleges") {
    selectedColleges.value.add(college as string);
  }
  pageLoading.value = false;
  activeTab.value = ($page?.minorsPage?.tabs?.[0]?.id as string) ?? "";
});
const fetchData = () => {
  const start = (currentPage.value - 1) * 12;
  const end = start + 12;
  paginatedData.value = resultsFiltered.value.slice(start, end);
};
watchEffect(() => {
  if (preparedSchema.value.length > 0) {
    const results = fuse.value.search({
      $and: preparedSchema.value,
    });
    resultsFiltered.value = results;
    fetchData();
  } else {
    resultsFiltered.value = results.value;
    fetchData();
  }
});
</script>

<style lang="scss" scoped>
.filter-div {
  z-index: 1;
}
.status {
  min-height: 700px;
}
.clear-btn {
  color: var(--primary, #8c1d40);
  background: none;
  border: none;
  margin: 0;
  padding: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.overlay-loading {
  &--show {
    position: relative;
    display: block;
    &:before {
      content: "";
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: var(--rds-dark-3);
      opacity: 0.8;
      z-index: 3;
    }
    .overlay-loader {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 4;
    }
  }
}
.tab-item {
  &--active {
    border-bottom: 4px solid var(--rds-primary) !important;
    color: var(--rds-primary) !important;
    padding-bottom: 4px !important;
  }
}
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.5s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  & > * {
    transition-duration: 200ms;
    transition-property: opacity, transform;
    transition-timing-function: cubic-bezier(0.6, 0.15, 0.35, 0.8);
  }
}

.fade-enter,
.fade-leave-to {
  & > * {
    opacity: 0;
  }
}
</style>
<style lang="scss">
:root {
  --delay: 100ms;
  --delayStep: 100ms;
}
.filter-categories {
  .form-check-input:checked {
    background-color: var(--rds-secondary);
    border-color: var(--rds-secondary);
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%23191919' d='M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z'/%3e%3c/svg%3e");
    background-size: 66%;
  }
}
.degree-search-pagination {
  --rds-pagination-color: var(--rds-dark-3);
  --rds-pagination-bg: transparent;
  --rds-pagination-border-radius: 50%;
  --rds-pagination-border-width: 0rem;
  --rds-pagination-border-color: transparent;
  --rds-pagination-hover-bg: var(--rds-primary);
  --rds-pagination-hover-color: var(--rds-white);
  --rds-pagination-hover-border-color: transparent;
  --rds-pagination-active-color: var(--rds-white);
  --rds-pagination-active-bg: var(--rds-primary);
  --rds-pagination-active-border-color: transparent;
  --rds-pagination-disabled-color: var(--rds-light-4);
  --rds-pagination-disabled-bg: transparent;
  --rds-pagination-disabled-border-color: transparent;
  --rds-pagination-focus-color: var(--rds-dark-3);
  --rds-pagination-focus-bg: transparent;
  --rds-pagination-padding-y: 0.5rem;
}
</style>
